// Turkish syllables, stress and vowel length for the "Sound it out" drill.
//
// Syllables are rule-based: every vowel is a syllable; a single consonant
// between vowels starts the next syllable (ki-tap); of a cluster, only the
// last consonant moves (İs-tan-bul, Türk-çe). Stress falls on the last
// syllable unless a word says otherwise (place names, loanwords, adverbs) or
// a suffix pulls it back (-yor, the negative -me/-ma). Length: ğ is silent and
// stretches the vowel before it (dağ, öğretmen); â î û are long; a few
// loanwords have unmarked long vowels, which the word list marks by hand.

const VOWELS = 'aeıioöuüâîû'
const HIGH = 'ıiuüîû'          // phonetically shorter when unstressed
const LONG_MARK = 'âîû'
export const lower = (s) => String(s || '').toLocaleLowerCase('tr-TR')
const isVowel = (c) => VOWELS.includes(c)

// Word → syllable strings, keeping the original casing.
export function syllabify(word) {
  const chars = Array.from(String(word || '').trim())
  const lc = chars.map(lower)
  const vIdx = lc.map((c, i) => (isVowel(c) ? i : -1)).filter(i => i >= 0)
  if (!chars.length) return []
  if (!vIdx.length) return [chars.join('')]
  const bounds = [0]
  for (let k = 0; k < vIdx.length - 1; k++) {
    const a = vIdx[k], b = vIdx[k + 1]
    const n = b - a - 1                     // consonants between the two vowels
    bounds.push(n <= 1 ? b - n : b - 1)     // 0 → after a; 1 → before it; 2+ → before the last one
  }
  bounds.push(chars.length)
  const out = []
  for (let i = 0; i < bounds.length - 1; i++) out.push(chars.slice(bounds[i], bounds[i + 1]).join(''))
  return out
}

// Where the stress goes when the word list doesn't say: last syllable, except
// that -yor takes the stress onto the syllable before it (ge-Lİ-yor).
export function guessStress(syls) {
  const n = syls.length
  if (n <= 1) return 0
  const yor = syls.findIndex((s, i) => i > 0 && lower(s).startsWith('yor'))
  if (yor > 0) return yor - 1
  return n - 1
}

// A pronunciation hint per letter, for the tap label and the sound strip.
const HINTS = {
  a: 'ah', e: 'eh', ı: '"uh" — the vowel in "roses"', i: 'ee', o: 'o', ö: 'like German ö', u: 'oo', ü: 'like German ü',
  â: 'long ah', î: 'long ee', û: 'long oo',
  b: 'b', c: 'j as in "jam"', ç: 'ch as in "church"', d: 'd', f: 'f', g: 'g as in "go"', ğ: 'silent — lengthens the vowel before',
  h: 'h, always sounded', j: 'zh as in "measure"', k: 'k', l: 'l', m: 'm', n: 'n', p: 'p', r: 'tapped r', s: 's', ş: 'sh',
  t: 't', v: 'v', y: 'y', z: 'z',
}
export const partLabel = (p) => `${p.text} · ${HINTS[lower(p.text)] || p.text}`
// What to speak for one letter: a vowel can be said alone; a consonant is
// only audible inside its syllable, so that is what we say.
export const soundOf = (p, syl) => (p.role === 'N' ? p.text : syl.text)

// Word → syllables with parts. `opts.stress` (syllable index) and `opts.long`
// (syllable indexes with an unmarked long vowel) come from the word list.
//   parts: { role: 'O' | 'N' | 'C', text, rom, silent?, long?, short?, factor? }
//   syllable: { text, parts, stressed, long, short, factor, estimated }
// A phrase is split on spaces first: each word is syllabified on its own (a
// space is not a consonant), carries its own stress when the list gives no
// mark, and its first syllable is flagged `wordBreak` so the drill can draw
// the gap. A marked `stress` indexes the whole syllable sequence.
export function decomposeWord(word, opts = {}) {
  const words = String(word || '').trim().split(/\s+/).filter(Boolean)
  const hasMark = Number.isInteger(opts.stress)
  const longSet = new Set(opts.long || [])
  const out = []
  words.forEach((w, wi) => {
    const syls = syllabify(w)
    const localStress = hasMark ? -1 : guessStress(syls)
    syls.forEach((text, k) => {
      const si = out.length
      const chars = Array.from(text)
      const lc = chars.map(lower)
      const v = lc.findIndex(isVowel)
      const parts = chars.map((ch, i) => {
        const c = lc[i]
        const role = v < 0 ? 'O' : i < v ? 'O' : i === v ? 'N' : 'C'
        return { role, text: ch, rom: HINTS[c] ? c : ch, silent: c === 'ğ' }
      })
      const stressed = hasMark ? si === opts.stress : k === localStress
      // Long: circumflex, a ğ right after the vowel (same syllable or the next
      // syllable's start, within the word), or marked in the word list.
      const nextStartsGh = lower(syls[k + 1] || '').startsWith('ğ')
      const ghAfter = v >= 0 && lc[v + 1] === 'ğ'
      const long = v >= 0 && (LONG_MARK.includes(lc[v]) || ghAfter || nextStartsGh || longSet.has(si))
      const short = v >= 0 && !long && !stressed && HIGH.includes(lc[v])
      let factor = 1
      if (stressed) factor *= 1.35
      if (long) factor *= 1.7
      if (short) factor *= 0.85
      if (v >= 0) Object.assign(parts[v], { long, short, factor })
      out.push({ text, parts, stressed, long, short, factor, estimated: !hasMark, ch: text, wordBreak: wi > 0 && k === 0 })
    })
  })
  return out
}

// "öː-ret-ˈmen": the word as it is said, stress and length marked; a space
// between words.
export const pronunciationLine = (syls) =>
  syls.map((s, i) => (i === 0 ? '' : s.wordBreak ? ' ' : '-') + (s.stressed ? 'ˈ' : '') + s.parts.map(p => (p.silent ? '' : p.text + (p.long ? 'ː' : ''))).join('')).join('')

// ── lenient scoring ──────────────────────────────────────────────────────────
// Sounds a learner (and the recogniser) swap freely count as near: half credit.
const NEAR = [['ı', 'i'], ['o', 'ö'], ['u', 'ü'], ['c', 'ç'], ['s', 'ş'], ['z', 'j'], ['t', 'd'], ['p', 'b'], ['k', 'g'], ['a', 'e']]
const near = (a, b) => NEAR.some(g => g.includes(a) && g.includes(b))
const strip = (s) => lower(s).replace(/ğ/g, '').replace(/â/g, 'a').replace(/î/g, 'i').replace(/û/g, 'u')
const cost = (a, b) => (a === b ? 0 : near(a, b) ? 0.5 : 1)

// Edit distance with near-sound substitutions, normalised to 0..1.
export function syllableScore(t, h) {
  const A = Array.from(strip(t.text)), B = Array.from(strip(h.text))
  if (!A.length || !B.length) return A.length === B.length ? 1 : 0
  const d = Array.from({ length: A.length + 1 }, (_, i) => [i, ...new Array(B.length).fill(0)])
  for (let j = 1; j <= B.length; j++) d[0][j] = j
  for (let i = 1; i <= A.length; i++) for (let j = 1; j <= B.length; j++) {
    d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost(A[i - 1], B[j - 1]))
  }
  return Math.max(0, 1 - d[A.length][B.length] / Math.max(A.length, B.length))
}
const MIN_SYL = 0.4

// Per-letter scores for colouring: each target letter looks for a match in
// the heard syllable among letters of the same role. ğ takes its vowel's score.
const partScores = (t, h) => {
  const used = new Set()
  const pick = (p) => {
    let best = 0, bi = -1
    h.parts.forEach((q, i) => {
      if (used.has(i) || q.role !== p.role || q.silent) return
      const s = lower(q.text) === lower(p.text) ? 1 : near(lower(p.text), lower(q.text)) ? 0.5 : 0
      if (s > best) { best = s; bi = i }
    })
    if (bi >= 0) used.add(bi)
    return best
  }
  const scores = t.parts.map(p => (p.silent ? null : pick(p)))
  const nucleus = t.parts.findIndex(p => p.role === 'N')
  return scores.map((s, i) => (s == null ? (nucleus >= 0 ? scores[nucleus] : 0) : s))
}

// Align the target's syllables with what was heard (any number of words).
export function alignWord(target, transcript, opts = {}) {
  const T = decomposeWord(target, opts)
  const H = String(transcript || '').split(/\s+/).filter(Boolean).flatMap(w => decomposeWord(w))
  const n = T.length, m = H.length
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))
  const back = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(''))
  for (let i = 1; i <= n; i++) for (let j = 1; j <= m; j++) {
    const s = syllableScore(T[i - 1], H[j - 1])
    const diag = dp[i - 1][j - 1] + (s >= MIN_SYL ? s : 0)
    const up = dp[i - 1][j], left = dp[i][j - 1]
    const best = Math.max(diag, up, left)
    dp[i][j] = best
    back[i][j] = best === diag ? 'd' : best === up ? 'u' : 'l'
  }
  const match = new Array(n).fill(null)
  let i = n, j = m
  while (i > 0 && j > 0) {
    const b = back[i][j]
    if (b === 'd') { if (syllableScore(T[i - 1], H[j - 1]) >= MIN_SYL) match[i - 1] = H[j - 1]; i--; j-- }
    else if (b === 'u') i--
    else j--
  }
  const syllables = T.map((t, k) => {
    const h = match[k]
    const ps = h ? partScores(t, h) : t.parts.map(() => 0)
    return { ...t, heard: h ? h.text : null, parts: t.parts.map((p, pi) => ({ ...p, score: ps[pi] })), score: h ? syllableScore(t, h) : 0 }
  })
  const total = n ? syllables.reduce((a, s) => a + s.score, 0) / n : 0
  return { syllables, total }
}
